/**
 * TaskCard Component (Super Branch Feature)
 * 
 * Enhanced task card with:
 * - Neon hover effects
 * - Priority-based color coding
 * - Tags display
 * - Quick actions (edit, delete, archive)
 * - Alarm indicator
 * - Responsive design
 * 
 * This component is drag-enabled for Kanban view
 * 
 * Usage:
 * <TaskCard task={task} onEdit={handleEdit} onDelete={handleDelete} />
 */

import type { Task, TaskPriority } from '../types';
import { motion } from 'framer-motion';
import { 
  MdEdit, 
  MdDelete, 
  MdArchive, 
  MdAlarm, 
  MdCalendarToday,
  MdLabel,
  MdDragIndicator
} from 'react-icons/md';
import { format, isPast, isToday } from 'date-fns';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useTheme } from '../hooks/useTheme';

interface TaskCardProps {
  task: Task;
  onEdit: (task: Task) => void;
  onDelete: (taskId: string) => void;
  onArchive?: (taskId: string) => void;
  className?: string;
}

export default function TaskCard({ 
  task, 
  onEdit, 
  onDelete, 
  onArchive,
  className = '' 
}: TaskCardProps) {
  const { theme } = useTheme();
  
  // Setup draggable with dnd-kit
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: task.id,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
  };
  
    // Priority color mapping with theme-aware accents
  const priorityColors: Record<TaskPriority, string> = {
    low: 'border-neon-green',
    medium: 'border-theme', // Use theme color for medium priority
    high: 'border-neon-pink',
  };

  const priorityBgColors: Record<TaskPriority, string> = {
    low: 'dark:bg-green-900/20 bg-green-100',
    medium: 'dark:bg-theme-10 bg-theme-10', // Use theme background for medium priority
    high: 'dark:bg-pink-900/20 bg-pink-100',
  };

  // Status badge colors
  const statusColors = {
    pending: 'bg-gray-500/20 text-gray-600 dark:text-gray-400',
    'in-progress': 'bg-theme-10 text-theme dark:text-theme',
    completed: 'bg-green-500/20 text-green-600 dark:text-green-400',
  };

  // Check if task has alarm
  const hasAlarm = task.alarm_time && !isPast(new Date(task.alarm_time));
  
  // Check if due date is past
  const isDuePast = task.due_date && isPast(new Date(task.due_date)) && task.status !== 'completed';
  const isDueToday = task.due_date && isToday(new Date(task.due_date));

  return (
    <motion.div
      ref={setNodeRef}
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: isDragging ? 0.5 : 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
      onClick={() => onEdit(task)}
      className={`
        relative
        p-4
        rounded-lg
        border-l-4
        border-2
        ${priorityColors[task.priority]}
        dark:border-theme
        border-theme
        shadow-md
        hover:shadow-neon
        transition-all
        duration-300
        cursor-pointer
        ${isDragging ? 'opacity-50' : ''}
        ${className}
      `}
      style={{
        backgroundColor: theme === 'dark' ? 'rgb(30 41 59)' : 'rgb(255 255 255)',
        transform: style.transform,
      }}
    >
      {/* Drag handle */}
      <div 
        {...listeners} 
        {...attributes}
        className="absolute top-2 right-2 cursor-grab active:cursor-grabbing touch-none"
      >
        <MdDragIndicator className="w-5 h-5 dark:text-gray-500 text-gray-400" />
      </div>

      {/* Header: Title and Status */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="
          text-lg 
          font-semibold 
          line-clamp-2
          flex-1
          pr-6
        "
        style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}
        >
          {task.title}
        </h3>
        
        {/* Status badge */}
        <span className={`
          px-2 py-1 
          rounded-full 
          text-xs 
          font-mono
          whitespace-nowrap
          ${statusColors[task.status]}
        `}>
          {task.status}
        </span>
      </div>

      {/* Description */}
      {task.description && (
        <p className="
          text-sm 
          mb-3
          line-clamp-2
        "
        style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}
        >
          {task.description}
        </p>
      )}

      {/* Tags */}
      {task.tags && task.tags.length > 0 && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <MdLabel className="text-neon-purple w-4 h-4" />
          {task.tags.map((tag, index) => (
            <span
              key={index}
              className="
                px-2 py-1
                rounded-md
                text-xs
                font-mono
                dark:bg-dark-hover
                bg-light-hover
                dark:text-neon-purple
                text-purple-600
              "
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Metadata row */}
      <div className="flex items-center gap-3 text-xs mb-3 flex-wrap" style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>
        {/* Priority badge */}
        <span className={`
          px-2 py-1 
          rounded-md 
          font-mono
          ${priorityBgColors[task.priority]}
        `}>
          {task.priority}
        </span>

        {/* Due date */}
        {task.due_date && (
          <div className={`
            flex items-center gap-1
            ${isDuePast ? 'text-red-500' : ''}
            ${isDueToday ? 'text-neon-yellow' : ''}
          `}>
            <MdCalendarToday className="w-3 h-3" />
            <span>{format(new Date(task.due_date), 'MMM dd, yyyy')}</span>
          </div>
        )}

        {/* Alarm indicator */}
        {hasAlarm && (
          <div className="flex items-center gap-1 text-theme">
            <MdAlarm className="w-3 h-3" />
            <span>{format(new Date(task.alarm_time!), 'HH:mm')}</span>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 pt-3 border-t-2 dark:border-theme border-theme">
        {/* Edit button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit(task);
          }}
          className="
            p-2
            rounded-md
            transition-all
            duration-200
            hover:scale-110
            hover:bg-theme-10
            hover:text-theme
          "
          style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}
          aria-label="Edit task"
          title="Edit task"
        >
          <MdEdit className="w-4 h-4" />
        </button>

        {/* Archive button */}
        {onArchive && !task.is_archived && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onArchive(task.id);
            }}
            className="
              p-2
              rounded-md
              transition-all
              duration-200
              hover:scale-110
              hover:bg-neon-purple/10
              hover:text-neon-purple
            "
            style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}
            aria-label="Archive task"
            title="Archive task"
          >
            <MdArchive className="w-4 h-4" />
          </button>
        )}

        {/* Delete button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (confirm('Are you sure you want to delete this task?')) {
              onDelete(task.id);
            }
          }}
          className="
            p-2
            rounded-md
            transition-all
            duration-200
            hover:scale-110
            hover:bg-red-500/10
            hover:text-red-500
            ml-auto
          "
          style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}
          aria-label="Delete task"
          title="Delete task"
        >
          <MdDelete className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}
